// LiveView — orchestrates the running state. Hosts cardiograph + timeline
// and the conditional pull progress / volume shred overlay.

import { Box, Stack } from '@mantine/core'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'
import { ContainerCard } from './ContainerCard'
import { HeroCardiograph } from './HeroCardiograph'
import { PhaseTimeline } from './PhaseTimeline'
import { PullProgress } from './PullProgress'
import {
  cardiographStateFromOperation,
} from './cardiographMotion'
import { MOTION, SURFACE } from './tokens'
import type { DockerOperation } from './types'
import { parseDockerPullOutput } from './parseDockerPullOutput'

interface Props {
  operation: DockerOperation
  image?: string
}

export function LiveView({ operation, image }: Props) {
  const state = cardiographStateFromOperation(operation)

  // Pull metadata: aggregate progress + summary text shown next to cardiograph
  const pullData = useMemo(() => {
    if (operation.current_phase !== 'pulling') return null
    const pullingPhase = operation.phases.find(p => p.name === 'pulling')
    if (!pullingPhase) return null
    return parseDockerPullOutput(pullingPhase.output ?? '')
  }, [operation])

  const pullProgress = useMemo(() => {
    if (!pullData || pullData.layers.length === 0) return undefined
    const sum = pullData.layers.reduce(
      (acc, l) => {
        if (l.status === 'done') return { current: acc.current + 1, total: acc.total + 1 }
        if (l.progress) {
          return {
            current: acc.current + l.progress.current / l.progress.total,
            total: acc.total + 1,
          }
        }
        return { current: acc.current, total: acc.total + 1 }
      },
      { current: 0, total: 0 },
    )
    return sum.total === 0 ? 0 : sum.current / sum.total
  }, [pullData])

  const cardiographMeta = useMemo(() => {
    if (operation.current_phase === 'pulling' && pullData && pullData.layers.length) {
      const done = pullData.layers.filter(l => l.status === 'done').length
      const pct = pullProgress ? Math.round(pullProgress * 100) : 0
      return `pulling ${done}/${pullData.layers.length} layers · ${pct}%`
    }
    if (operation.current_phase) {
      return operation.current_phase.replace(/_/g, ' ')
    }
    if (operation.status === 'success') return 'healthy'
    if (operation.status === 'failed') return 'failed'
    return undefined
  }, [operation, pullData, pullProgress])

  const pullingPhaseEntry =
    operation.current_phase === 'pulling'
      ? operation.phases.find(p => p.name === 'pulling')
      : undefined
  const pullingOutput = pullingPhaseEntry?.output ?? ''
  const pullingStartedAt = pullingPhaseEntry?.started_at

  return (
    <Stack gap="md">
      <ContainerCard
        containerName={operation.container_name}
        image={image}
        operationStartedAt={operation.started_at}
      />

      <Box
        p={12}
        style={{
          background: SURFACE.card,
          border: `1px solid ${SURFACE.cardBorder}`,
          borderRadius: 8,
        }}
      >
        <HeroCardiograph
          state={state}
          meta={cardiographMeta}
          pullProgress={pullProgress}
        />
      </Box>

      <AnimatePresence mode="wait">
        {operation.current_phase === 'pulling' && (
          <motion.div
            key="pull-progress"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={MOTION.viewSwap}
            style={{ overflow: 'hidden' }}
          >
            <PullProgress output={pullingOutput} startedAt={pullingStartedAt} />
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        <PhaseTimeline operation={operation} />
      </Box>
    </Stack>
  )
}
